import { defineStore } from 'pinia';
import crmApi from '../api/crm';

const getLastFunnelStorageKey = () => {
  const accountId = window.location.pathname.split('/')[3];
  return `chatwoot_crm_last_funnel_id_${accountId}`;
};

export const useCrmStore = defineStore('crm', {
  state: () => ({
    funnels: [],
    currentFunnelId: null,
    stages: [],
    opportunities: [],
    selectedOpportunityId: null,
    isLoading: false,
    error: null,
  }),

  getters: {
    opportunitiesByStage: state => {
      const map = {};
      state.stages.forEach(stage => {
        map[stage.id] = [];
      });
      state.opportunities.forEach(opportunity => {
        const stageId = opportunity.funnel_stage_id;
        if (map[stageId]) {
          map[stageId].push(opportunity);
        } else {
          map[stageId] = [opportunity];
        }
      });
      return map;
    },

    selectedOpportunity: state =>
      state.opportunities.find(o => o.id === state.selectedOpportunityId) ||
      null,

    currentFunnel: state =>
      state.funnels.find(f => f.id === state.currentFunnelId) || null,
  },

  actions: {
    async fetchFunnels() {
      this.isLoading = true;
      this.error = null;
      try {
        const { data } = await crmApi.getFunnels();
        this.funnels = data.payload || data;
        if (this.currentFunnelId === null && this.funnels.length > 0) {
          const activeFunnels = this.funnels.filter(f => f.active !== false);
          const pool = activeFunnels.length > 0 ? activeFunnels : this.funnels;
          const storedId = localStorage.getItem(getLastFunnelStorageKey());
          const lastFunnelId = storedId ? Number(storedId) : null;
          const remembered = pool.find(f => f.id === lastFunnelId);
          await this.selectFunnel((remembered || pool[0]).id);
        }
      } catch (err) {
        this.error = err.message || 'Failed to fetch funnels';
      } finally {
        this.isLoading = false;
      }
    },

    async selectFunnel(funnelId) {
      this.currentFunnelId = funnelId;
      localStorage.setItem(getLastFunnelStorageKey(), funnelId);
      await Promise.all([
        this.fetchStages(funnelId),
        this.fetchOpportunities({ funnel_id: funnelId }),
      ]);
    },

    async fetchStages(funnelId) {
      this.isLoading = true;
      this.error = null;
      try {
        const { data } = await crmApi.getStages(funnelId);
        this.stages = data.payload || data;
      } catch (err) {
        this.error = err.message || 'Failed to fetch stages';
      } finally {
        this.isLoading = false;
      }
    },

    async fetchOpportunities(params) {
      this.isLoading = true;
      this.error = null;
      const resolvedParams = params || { funnel_id: this.currentFunnelId };
      try {
        const { data } = await crmApi.getOpportunities(resolvedParams);
        this.opportunities = data.payload || data;
      } catch (err) {
        this.error = err.message || 'Failed to fetch opportunities';
      } finally {
        this.isLoading = false;
      }
    },

    async createOpportunity(opportunityData) {
      this.isLoading = true;
      this.error = null;
      try {
        const { data } = await crmApi.createOpportunity(opportunityData);
        const opportunity = data.payload || data;
        this.opportunities.push(opportunity);
        return opportunity;
      } catch (err) {
        this.error = err.message || 'Failed to create opportunity';
        return null;
      } finally {
        this.isLoading = false;
      }
    },

    async updateOpportunity(id, opportunityData) {
      this.isLoading = true;
      this.error = null;
      try {
        const { data } = await crmApi.updateOpportunity(id, opportunityData);
        const updated = data.payload || data;
        const index = this.opportunities.findIndex(o => o.id === id);
        if (index !== -1) {
          this.opportunities[index] = updated;
        }
        return updated;
      } catch (err) {
        this.error = err.message || 'Failed to update opportunity';
        return null;
      } finally {
        this.isLoading = false;
      }
    },

    async moveOpportunity(id, stageId) {
      this.isLoading = true;
      this.error = null;
      try {
        await crmApi.moveOpportunityStage(id, stageId);
        const opportunity = this.opportunities.find(o => o.id === id);
        if (opportunity) {
          opportunity.funnel_stage_id = stageId;
        }
      } catch (err) {
        this.error = err.message || 'Failed to move opportunity';
      } finally {
        this.isLoading = false;
      }
    },

    async deleteOpportunity(id) {
      this.isLoading = true;
      this.error = null;
      try {
        await crmApi.deleteOpportunity(id);
        this.opportunities = this.opportunities.filter(o => o.id !== id);
        if (this.selectedOpportunityId === id) {
          this.selectedOpportunityId = null;
        }
      } catch (err) {
        this.error = err.message || 'Failed to delete opportunity';
      } finally {
        this.isLoading = false;
      }
    },

    async createFunnel(name) {
      try {
        const { data } = await crmApi.createFunnel({ name });
        const funnel = data.payload || data;
        this.funnels.push(funnel);
        await this.selectFunnel(funnel.id);
        return funnel;
      } catch (err) {
        this.error = err.message || 'Failed to create funnel';
        return null;
      }
    },

    selectOpportunity(id) {
      this.selectedOpportunityId = id;
    },
  },
});
